// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'redux-pender';
import * as PartnersAPI from 'lib/api/partners';

const GET_RECENT_PARTNERS = 'partner/GET_RECENT_PARTNERS';
const PREFETCH_RECENT_PARTNERS = 'partner/PREFETCH';
const REVEAL_PREFETCHED = 'partner/REVEAL_PREFETCHED';
const CLEAR_USER_PARTNERS = 'partner/CLEAR_USER_PARTNERS';
const GET_USER_PARTNERS = 'partner/GET_USER_PARTNERS';
const PREFETCH_USER_PARTNERS = 'partner/PREFETCH_USER_PARTNERS';
const GET_TRENDING_PARTNERS = 'partner/GET_TRENDING_PARTNERS';
const PREFETCH_TRENDING_PARTNERS = 'partner/PREFETCH_TRENDING_PARTNERS';
const GET_TAG_PARTNERS = 'partner/GET_TAG_PARTNERS';
const PREFETCH_TAG_PARTNERS = 'partner/PREFETCH_TAG_PARTNERS';
const GET_CATEGORY_PARTNERS = 'partner/GET_CATEGORY_PARTNERS';
const PREFETCH_CATEGORY_PARTNERS = 'partner/PREFETCH_CATEGORY_PARTNERS';
const CLEAR_CATEGORY_PARTNERS = 'partner/CLEAR_CATEGORY_PARTNERS';
const CLEAR_TAG_PARTNERS = 'partner/CLEAR_TAG_PARTNERS';
const GET_TEMP_PARTNERS = 'partner/GET_TEMP_PARTNERS';
const PREFETCH_TEMP_PARTNERS = 'partner/PREFETCH_TEMP_PARTNERS';
const REMOVE_TEMP_PARTNER = 'partner/REMOVE_TEMP_PARTNER';
const FLUSH_LIST = 'partner/FLUSH_LIST';

// potential improvement :: remove duplicates
export const actionCreators = {
  getRecentPartners: createAction(GET_RECENT_PARTNERS, PartnersAPI.getPublicPartners),
  prefetchRecentPartners: createAction(PREFETCH_RECENT_PARTNERS, PartnersAPI.getPublicPartners),
  revealPrefetched: createAction(REVEAL_PREFETCHED, (type: string) => type),
  clearUserPartners: createAction(CLEAR_USER_PARTNERS),
  getUserPartners: createAction(
    GET_USER_PARTNERS,
    PartnersAPI.getUserPartners,
    (meta: { username: string, tag?: string }) => meta,
  ),
  prefetchUserPartners: createAction(PREFETCH_USER_PARTNERS, PartnersAPI.getUserPartners),
  getTrendingPartners: createAction(GET_TRENDING_PARTNERS, PartnersAPI.getTrendingPartners),
  prefetchTrendingPartners: createAction(
    PREFETCH_TRENDING_PARTNERS,
    PartnersAPI.getTrendingPartners,
  ),
  getTagPartners: createAction(
    GET_TAG_PARTNERS,
    PartnersAPI.getPublicPartnersByTag,
    (meta: { tag: string }) => meta,
  ),
  prefetchTagPartners: createAction(PREFETCH_TAG_PARTNERS, PartnersAPI.getPublicPartnersByTag),
  getCategoryPartners: createAction(
    GET_CATEGORY_PARTNERS,
    PartnersAPI.getPublicPartnersByCategory,
    (meta: { category: string, sort?: string }) => meta,
  ),
  prefetchCategoryPartners: createAction(
    PREFETCH_CATEGORY_PARTNERS,
    PartnersAPI.getPublicPartnersByCategory,
  ),
  clearCategoryPartners: createAction(CLEAR_CATEGORY_PARTNERS),
  clearTagPartners: createAction(CLEAR_TAG_PARTNERS),
  getTempPartners: createAction(GET_TEMP_PARTNERS, PartnersAPI.getTempPartners),
  prefetchTempPartners: createAction(PREFETCH_TEMP_PARTNERS, PartnersAPI.getTempPartners),
  removeTempPartner: createAction(REMOVE_TEMP_PARTNER, (partnerId: string) => partnerId),
  flushList: createAction(FLUSH_LIST),
};

export type PartnerItem = {
  id: string,
  title: string,
  body: string,
  thumbnail: string,
  is_markdown: boolean,
  is_private: boolean,
  released_at: string,
  created_at: string,
  updated_at: string,
  tags: string[],
  categories: any[],
  url_slug: string,
  likes: number,
  comments_count: number,
  user: {
    id: string,
    username: string,
    partner_name: string,
    short_bio: string,
    thumbnail: ?string,
  },
  meta: any,
};

export type PartneringSet = {
  partners: ?(PartnerItem[]),
  prefetched: ?(PartnerItem[]),
  end: boolean,
};

export type Partnering = {
  trendingMap: {
    [id: string]: boolean,
  },
  trending: PartneringSet,
  recent: PartneringSet,
  user: PartneringSet & { currentUsername: ?string, currentTag: ?string },
  tag: PartneringSet & { currentTag: ?string },
  category: PartneringSet & { currentCategory: ?string },
  temp: PartneringSet,
};

type RevealPrefetchedAction = ActionType<typeof actionCreators.revealPrefetched>;
type PartnersResponseAction = {
  type: string,
  payload: {
    data: PartnerItem[],
    category?: ?string,
    sort?: ?string,
  },
  meta: any,
};
type RemoveTempPartnerAction = ActionType<typeof actionCreators.removeTempPartner>;

export interface PartneringActionCreators {
  getRecentPartners(cursor: string, category: string, sort: string): any;

  prefetchRecentPartners(cursor: string, category: string, sort: string): any;

  revealPrefetched(type: string): any;

  getUserPartners(payload: PartnersAPI.GetUserPartnersPayload): any;

  prefetchUserPartners(payload: PartnersAPI.GetUserPartnersPayload): any;

  getTrendingPartners(): any;

  prefetchTrendingPartners(offset: number): any;

  getTagPartners(payload: PartnersAPI.GetPublicPartnersByTagPayload): any;

  prefetchTagPartners(payload: PartnersAPI.GetPublicPartnersByTagPayload): any;

  getCategoryPartners(payload: PartnersAPI.GetPublicPartnersByCategoryPayload): any;

  prefetchCategoryPartners(payload: PartnersAPI.GetPublicPartnersByCategoryPayload): any;

  getTempPartners(payload: PartnersAPI.GetTempPartnersPayload): any;

  prefetchTempPartners(payload: PartnersAPI.GetTempPartnersPayload): any;

  clearCategoryPartners(): any;

  clearTagPartners(): any;

  clearUserPartners(): any;

  removeTempPartner(partnerId: string): any;

  flushList(): any;
}

const initialPartneringSet = {
  partners: null,
  prefetched: null,
  end: false,
};

const initialState: Partnering = {
  trendingMap: {},
  trending: initialPartneringSet,
  recent: initialPartneringSet,
  user: { ...initialPartneringSet, currentUsername: null, currentTag: null },
  tag: { ...initialPartneringSet, currentTag: null },
  category: { ...initialPartneringSet, currentCategory: null },
  temp: initialPartneringSet,
};

const reducer = handleActions(
  {
    [REVEAL_PREFETCHED]: (state, action: RevealPrefetchedAction) => {
      return produce(state, draft => {
        const { payload } = action;
        const { partners, prefetched } = draft[payload];
        if (partners && prefetched) {
          partners.push(...prefetched);
          draft[payload].prefetched = null;
        }
      });
    },
    [CLEAR_USER_PARTNERS]: state => {
      return produce(state, draft => {
        draft.user = {
          partners: null,
          prefetched: null,
          end: false,
          currentUsername: null,
          currentTag: null,
        };
      });
    },
    [CLEAR_TAG_PARTNERS]: state => {
      return produce(state, draft => {
        draft.tag = {
          partners: null,
          prefetched: null,
          end: false,
          currentTag: null,
        };
      });
    },
    [CLEAR_CATEGORY_PARTNERS]: state => {
      return produce(state, draft => {
        draft.category = {
          partners: null,
          prefetched: null,
          end: false,
          currentCategory: null,
        };
      });
    },
    [REMOVE_TEMP_PARTNER]: (state, { payload }: RemoveTempPartnerAction) => {
      return produce(state, draft => {
        if (!draft.temp.partners) return;
        draft.temp.partners = draft.temp.partners.filter(p => p.id !== payload);
      });
    },
    [FLUSH_LIST]: state => {
      return initialState;
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_RECENT_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { category, sort } = action.payload;
      return produce(state, draft => {
        draft.recent = {
          category,
          sort,
          end: false,
          partners: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_RECENT_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.recent.prefetched = data;
        if (data && data.length === 0) {
          draft.recent.end = true;
        }
      });
    },
  },
  {
    type: GET_TAG_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      return produce(state, draft => {
        draft.tag = {
          end: action.payload.data.length < 20,
          partners: action.payload.data,
          prefetched: null,
          currentTag: action.meta.tag,
        };
      });
    },
  },
  {
    type: PREFETCH_TAG_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.tag.prefetched = data;
        if (data && data.length === 0) {
          draft.tag.end = true;
        }
      });
    },
  },
  {
    type: GET_CATEGORY_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      return produce(state, draft => {
        draft.category = {
          end: action.payload.data.length < 20,
          partners: action.payload.data,
          prefetched: null,
          currentCategory: action.meta.category,
          sort: action.meta.sort,
        };
      });
    },
  },
  {
    type: PREFETCH_CATEGORY_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.category.prefetched = data;
        if (data && data.length === 0) {
          draft.category.end = true;
        }
      });
    },
  },
  {
    type: GET_TRENDING_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      return produce(state, draft => {
        action.payload.data.forEach(partner => {
          draft.trendingMap[partner.id] = true;
        });
        draft.trending = {
          end: false,
          partners: action.payload.data,
          prefetched: null,
        };
      });
    },
  },
  {
    type: PREFETCH_TRENDING_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        const filtered = data.filter(partner => !state.trendingMap[partner.id]);
        draft.trending.prefetched = filtered;
        filtered.forEach(partner => {
          draft.trendingMap[partner.id] = true;
        });
        if (data && data.length === 0) {
          draft.trending.end = true;
        }
      });
    },
  },
  {
    type: GET_USER_PARTNERS,
    onPending: (state: Partnering) => {
      return produce(state, draft => {
        draft.user.end = false;
        draft.user.prefetched = null;
      });
    },
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      return produce(state, draft => {
        draft.user.partners = action.payload.data;
        draft.user.currentUsername = action.meta.username;
        if (action.meta.tag) {
          draft.user.currentTag = action.meta.tag;
        }
        if (action.payload.data.length < 20) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_USER_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.user.prefetched = data;
        if (data && data.length === 0) {
          draft.user.end = true;
        }
      });
    },
  },
  {
    type: GET_TEMP_PARTNERS,
    onPending: (state: Partnering) => {
      return produce(state, draft => {
        draft.temp.end = false;
        draft.temp.prefetched = null;
      });
    },
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      return produce(state, draft => {
        draft.temp.partners = action.payload.data;
        if (action.payload.data.length < 20) {
          draft.temp.end = true;
        }
      });
    },
  },
  {
    type: PREFETCH_TEMP_PARTNERS,
    onSuccess: (state: Partnering, action: PartnersResponseAction) => {
      const { data } = action.payload;
      return produce(state, draft => {
        draft.temp.prefetched = data;
        if (data && data.length === 0) {
          draft.temp.end = true;
        }
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/partnering.js